import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/hooks/useAuth';
import { retryClaims } from '@elka/hooks';
import { LoadingSpinner, NotProvisionedScreen } from '@elka/ui';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, isAuthorized, isUnprovisioned, signOut } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Not signed in → login, preserving where they were headed.
  if (!user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  // Claim OR domain fallback (transition). Authorized users see the app — check
  // this BEFORE isUnprovisioned so a domain-whitelisted user without claims yet
  // isn't bricked on the not-provisioned screen during the transition.
  if (isAuthorized) {
    return <>{children}</>;
  }

  // Signed in but no tenant claim yet → not-provisioned screen (self-serve retry
  // after onboarding, or sign out to try another account).
  if (isUnprovisioned) {
    return (
      <NotProvisionedScreen
        email={user.email}
        onSignOut={signOut}
        onRetry={async () => {
          const provisioned = await retryClaims(user);
          if (provisioned) window.location.reload();
        }}
      />
    );
  }

  // Authenticated but not authorized (no claim + not on the transition
  // whitelist) → back to login.
  return <Navigate to="/login" replace />;
}
